import React from "react";
import { Modal } from "antd";
import { callApi } from "./superAgentUtil";
import formatMsg from "./formatMsg";

export interface RecurringImpactInfo {
    impacted: boolean;
    count: number;
    label: string;
    message?: string;
    // Only a pricing pack carries the frequency / "bill every" / generation timing, so only it can
    // move a cycle boundary onto a period already billed. The backend decides; this is not re-derived
    // from the type here, so the rule lives in one place.
    movesCycleBoundaries?: boolean;
}

/**
 * Ask the backend how many LIVE recurring bookings an edit to this entity would reach.
 *
 * A pricing pack, tax, discount or recurring additional charge is re-read by the invoice cron on
 * every billing cycle, so editing one changes the invoices of bookings that already exist — not
 * only the bookings made from here on. Admins have no way to see that from the edit form, which is
 * what this warning is for.
 *
 * Fails OPEN: any error returns "no impact" so the save still goes through. This is advisory
 * information, never a gate — the entity's own validation stays the only thing that can stop a save.
 */
export async function checkRecurringImpact(firebase: any, type: string, id: string): Promise<RecurringImpactInfo> {
    try {
        const res = await callApi(firebase, "get", `bookingoApi/package/recurringImpact/${type}/${id}`, {});
        if (res && res.status === 200 && res.body && res.body.data) {
            return res.body.data as RecurringImpactInfo;
        }
    } catch (e) {
        // swallow — fail open
    }
    return { impacted: false, count: 0, label: type };
}

/**
 * Run `proceed()` only after the admin has seen what their edit will touch.
 *
 * When nothing recurring is affected — a new entity, a one-time pack, an entity no live booking
 * uses — `proceed()` is called straight through with no dialog, so the common path is unchanged.
 */
export async function confirmRecurringImpact(
    firebase: any,
    type: string,
    id: string,
    proceed: () => void
): Promise<void> {
    const info = await checkRecurringImpact(firebase, type, id);
    if (!info.impacted) {
        proceed();
        return;
    }

    // The entity phrase is translated here rather than taken from the backend's English `label`, and
    // it carries its OWN demonstrative ("this pricing pack" / "باقة الأسعار هذه") because gender
    // agreement differs per noun — interpolating a bare noun into a fixed "este {x}" is wrong in both
    // Spanish and Arabic. Count and pluralisation live in the ICU string, so Arabic gets all six of
    // its plural categories rather than the two English needs.
    const entity = formatMsg("recurringImpactEntity." + type);
    const paragraphs = [formatMsg("recurringImpactBody", { count: info.count, entity })];
    if (info.movesCycleBoundaries) paragraphs.push(formatMsg("recurringImpactCycleShift"));

    Modal.confirm({
        title: formatMsg("recurringImpactTitle", { count: info.count }),
        // Real paragraphs, not one run-on block: the second sentence is a different (and more
        // serious) consequence than the first, and reads as boilerplate when they are merged.
        content: React.createElement(
            "div",
            null,
            paragraphs.map((text, i) =>
                React.createElement("p", { key: i, style: { margin: i === 0 ? "0 0 12px" : 0 } }, text)
            )
        ),
        okText: formatMsg("recurringImpactSave"),
        cancelText: formatMsg("cancel"),
        // Block body, NOT `() => proceed()`. antd's ActionButton closes the dialog only when onOk
        // returns a falsy value or a promise; `proceed()` hands back the dispatched Redux action,
        // which is truthy and not thenable, so a concise arrow leaves the dialog stuck open with a
        // dead OK button. Returning undefined is what makes it close.
        onOk: () => {
            proceed();
        },
    });
}
